import { WithholdingLineInput, WithholdingType } from './withholding.interface';

export type PaymentRunStatus =
  'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'EXECUTED' | 'CANCELLED' | 'REVERSED';
export type PaymentRunMethod = 'BEFTN' | 'RTGS' | 'NPSB' | 'CHEQUE' | 'MANUAL';
export type BankFileFormat = 'BEFTN' | 'RTGS' | 'NPSB';
export type SupplierBankKind = 'BANK' | 'MFS';
export type AllocationCreditType = 'SUPPLIER_ADVANCE' | 'PURCHASE_DEBIT_NOTE';

export const BANK_FILE_METHODS: PaymentRunMethod[] = ['BEFTN', 'RTGS', 'NPSB'];

export interface OpenCredit {
  creditType: string;
  creditId: string;
  number?: string | null;
  date?: string | null;
  amount: number;
  available: number;
  partyId?: string | null;
}

export interface WithholdingProposalLine {
  type: WithholdingType;
  ruleId: string;
  ruleCode?: string | null;
  baseAmount: number;
  amount: number;
}

export interface ProposalLine {
  supplierBillId: string;
  billNumber: string;
  supplierInvoiceRef?: string | null;
  supplierId: string;
  supplierName?: string | null;
  billDate?: string | null;
  dueDate?: string | null;
  outstanding: number;
  settleAmount: number;
  discountPct?: number | null;
  discountEligible: boolean;
  discountAmount: number;
  paymentAmount: number;
  withheldAmount: number;
  netAmount: number;
  withholdings: WithholdingProposalLine[];
  warnings: string[];
}

export interface SupplierCredits {
  supplierId: string;
  supplierName?: string | null;
  credits: OpenCredit[];
}

export interface Proposal {
  lines: ProposalLine[];
  credits: SupplierCredits[];
  totalSettle: number;
  blockedBillCount: number;
  /** Due bills left out because a cheque is already issued against them. */
  chequeExcludedCount?: number;
  warnings: string[];
}

export interface ProposeRequest {
  institutionId: string;
  bankAccountId: string;
  method: PaymentRunMethod;
  paymentDate: string;
  dueOnOrBefore?: string;
  supplierIds?: string[];
  perSupplierCap?: number;
  totalCap?: number;
}

export interface CreditInput { creditType: AllocationCreditType; creditId: string; amount: number }

export interface RunLineInput {
  supplierBillId: string;
  settleAmount: number;
  discountAmount?: number;
  credits?: CreditInput[];
  withholdings?: WithholdingLineInput[];
}

export interface RunSaveRequest {
  institutionId: string;
  bankAccountId: string;
  method: PaymentRunMethod;
  paymentDate: string;
  chequeBookId?: string;
  bankFileTemplateId?: string;
  notes?: string;
  dueOnOrBefore?: string;
  supplierIds?: string[];
  perSupplierCap?: number;
  totalCap?: number;
  lines: RunLineInput[];
}

export interface RunCreditLine {
  id: string; creditType: AllocationCreditType; creditId: string; creditNumber?: string | null;
  amount: number; allocationId?: string | null; reversed: boolean;
}

export interface RunWithholding {
  type: WithholdingType; ruleId: string; ruleCode?: string | null; baseAmount: number; amount: number;
  edited: boolean; certificateNumber?: string | null;
}

/** B-23: where this line's money goes was added, edited or removed lately, or differs from the last payment. A warning only. */
export interface BankChangeFlag {
  recentlyChanged: boolean;
  /** The paid-to account was saved again after the last payment to this supplier, however long ago. */
  changedSinceLastPayment: boolean;
  changeKind: 'ADDED' | 'EDITED' | 'REMOVED';
  changedAt?: string | null;
  changedByName?: string | null;
  bankName?: string | null;
  /** Last 4 digits only. */
  maskedAccount?: string | null;
  differsFromLastPayment: boolean;
  lastPaidBankName?: string | null;
  lastPaidMaskedAccount?: string | null;
  windowDays: number;
}

export interface RunLine {
  id: string;
  supplierBillId: string;
  billNumber?: string | null;
  supplierInvoiceRef?: string | null;
  supplierId: string;
  supplierName?: string | null;
  billDate?: string | null;
  dueDate?: string | null;
  outstanding: number;
  settleAmount: number;
  creditAmount: number;
  discountAmount: number;
  /** Discount beyond the supplier's terms or cap: the approver should look at it. */
  discountOverride?: boolean;
  paymentAmount: number;
  withheldAmount: number;
  netAmount: number;
  supplierPaymentId?: string | null;
  paymentNumber?: string | null;
  reversed: boolean;
  credits: RunCreditLine[];
  withholdings: RunWithholding[];
  /** Present only when the supplier's bank account needs a second look. */
  bankChange?: BankChangeFlag | null;
  /** Read-side notes for this line, e.g. a supplier with no VDS / TDS rule (B-24). */
  warnings?: string[];
}

export interface RunApproval {
  approverId: string; approverName?: string | null; decision: string; comment?: string | null; decidedAt: string;
}

export interface RunCheque {
  id: string; chequeNumber: string; supplierId: string; partyName?: string | null; amount: number;
  status: string; chequeDate?: string | null; printedAt?: string | null;
}

export interface PaymentRun {
  id: string;
  institutionId: string;
  runNo: string;
  status: PaymentRunStatus;
  bankAccountId: string;
  bankAccountLabel?: string | null;
  method: PaymentRunMethod;
  paymentDate: string;
  dueOnOrBefore?: string | null;
  perSupplierCap?: number | null;
  totalCap?: number | null;
  chequeBookId?: string | null;
  bankFileTemplateId?: string | null;
  notes?: string | null;
  rejectReason?: string | null;
  approvalsRequired: number;
  totalSettle: number;
  totalCredits: number;
  totalDiscount: number;
  totalWithheld: number;
  totalNet: number;
  submittedAt?: string | null;
  executedAt?: string | null;
  bankFileGeneratedAt?: string | null;
  createdBy?: string | null;
  submittedBy?: string | null;
  lastEditedBy?: string | null;
  /** The caller made, edited or submitted this run, so they cannot approve it. */
  callerIsMaker?: boolean;
  callerHasApproved?: boolean;
  createdAt?: string | null;
  lines: RunLine[];
  approvals: RunApproval[];
  cheques: RunCheque[];
}

export interface PaymentRunSummary {
  id: string; runNo: string; status: PaymentRunStatus; method: PaymentRunMethod; paymentDate: string;
  bankAccountLabel?: string | null; totalSettle: number; totalNet: number; lineCount: number; createdAt?: string | null;
}

export interface PaymentRunPage {
  content: PaymentRunSummary[]; number: number; totalPages: number; totalElements: number;
}

export interface BankFileRow {
  serial: number; supplierId: string; supplierName?: string | null; bankName?: string | null;
  branchName?: string | null; routingNumber?: string | null; accountNumber?: string | null;
  accountName?: string | null; amount: number;
}

export interface BankFilePreview {
  templateId: string; templateName: string; format: BankFileFormat; rows: BankFileRow[];
  total: number; warnings: string[]; fileName: string;
}

export interface BlockedBill {
  billId: string; billNumber: string; supplierInvoiceRef?: string | null; supplierId: string;
  supplierName?: string | null; billDate: string; dueDate: string; totalAmount: number;
  blockReason?: string | null; ageDays: number;
  /** FW-3B: the hold is (also) an approval, decided in the Approvals inbox and never released by hand. */
  approvalHold?: boolean;
}

export interface PaymentRunSettings { discountReceivedAccountId?: string | null; discountReceivedAccountName?: string | null }

export interface SupplierBankAccount {
  id?: string;
  institutionId: string;
  supplierId: string;
  kind: SupplierBankKind;
  bankName?: string | null;
  branchName?: string | null;
  routingNumber?: string | null;
  accountNumber?: string | null;
  accountName?: string | null;
  mfsProvider?: string | null;
  mfsWallet?: string | null;
  defaultAccount?: boolean;
  notes?: string | null;
}

export interface SupplierPaymentProfile {
  supplierId: string; earlyDiscountPct?: number | null; earlyDiscountDays?: number | null;
  accounts: SupplierBankAccount[];
}

export interface ApprovalBand {
  id?: string; institutionId: string; sequenceNo: number; minAmount: number; maxAmount?: number | null;
  approvalsRequired: number;
}

export interface BankFileTemplate {
  id?: string;
  institutionId: string;
  name: string;
  bankName?: string | null;
  format: BankFileFormat;
  delimiter: string;
  hasHeader: boolean;
  columns: string[];
  dateFormat: string;
  minAmount?: number | null;
  maxAmount?: number | null;
  notes?: string | null;
}

export interface BankFileFieldInfo { key: string; label: string }
export interface TemplateDefaultsResult { created: number; skipped: number }
